import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { ProjectStageApprovalService } from '@/app/services/projects/project-stage-approval.service';
import { ProjectStageApprover } from '@/app/interfaces/project-stage-approver.interface';
import { DomSanitizer } from '@angular/platform-browser';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { DateFormatService } from '@/app/services/date-format.service';
import { InternalApprovalStatus } from '@/app/enums/status.enum';
import { ReplaceDashAndSentenceCasePipe } from '../pipes/replace-dash-and-sentence-case.pipe';
import { ApprovalFeedbackComponent } from '@/app/components/global/dialog-boxes/approval-feedback/approval-feedback.component';
import { User } from '@/app/interfaces/user-info.interface';
import { ReloadPlanDataService } from '@/app/services/shared/reload-plan-data.service';
interface ApprovalDetail {
  feedback: string | null;
  status: string;
  userName: string;
  userTitle: string | null;
  statusUpdatedAt: string;
  createdAt: string;
  requestedApproverId: number;
  note: string
}
@Component({
  selector: 'app-approval-status-view',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    ReplaceDashAndSentenceCasePipe,
    ApprovalFeedbackComponent,
  ],
  templateUrl: './approval-status-view.component.html',
  styleUrl: './approval-status-view.component.css',
})
export class ApprovalStatusViewComponent implements OnInit, OnChanges {
  // Props from the parent
  @Input() projectId!: number;
  @Input() stage!: number;
  user_info: User = localStorage.getItem('user_info')
    ? JSON.parse(localStorage.getItem('user_info') as string)
    : null;
  approvals: ProjectStageApprover[] = [];
  approvalStatus: any;
  approvalDetails: ApprovalDetail[] = [];
  APPROVAL_STATUS = InternalApprovalStatus;

  // Inject HttpClient to make requests
  constructor(
    private http: HttpClient,
    private approvalService: ProjectStageApprovalService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private projectsService: ProjectsService,
    private dateFormat: DateFormatService,
    private reloadPlanDataService: ReloadPlanDataService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId']) {
      this.checkStageOneApproval();
    }
  }

  // OnInit lifecycle hook to fetch approvals when component loads
  ngOnInit(): void {
    this.checkStageOneApproval();
    this.reloadPlanDataService.reloadPlanData$.subscribe((reload) => {
      if (reload) {
        this.checkStageOneApproval();
      }
    });
  }

  async checkStageOneApproval(): Promise<void> {
    if (!this.projectId) {
      return;
    }

    const data = {
      project_id: this.projectId,
      stage: this.stage,
    };

    try {
      const temp = await this.projectsService
        .getCurrentStageApproval(data)
        .toPromise();

      if (temp.data.length > 0) {
        this.approvalDetails = temp?.data?.map((item: any) => ({
          feedback: item.feedback,
          note: item?.notes,
          status: item.status,
          userName: item.user.name,
          userTitle: item.user.title,
          requestedApproverId: item?.user?.id,
          statusUpdatedAt: this.dateFormat.formatDate(item.updated_at),
          createdAt: item.created_at,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  }

  getSanitizedString(data: string) {
    return this.sanitizer.bypassSecurityTrustHtml(data);
  }

  openApprovalFeedbackDialog(): void {
    const dialogRef = this.dialog.open(ApprovalFeedbackComponent, {
      width:'80vw',
      data: {
        stage: this.stage,
        projectId: this.projectId,
      }, 
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.success && result.updatedApproval) {
        // Call updateApprovalList with the single approval object
        this.reloadPlanDataService.reloadPlanData();
        this.updateApprovalList(result.updatedApproval);
      } else {
        console.warn('No valid approval found in the response:', result);
      }
    });
  }
  // Helper method to handle approval updates
  private updateApprovalList(updatedApprovals: any[]): void {
    updatedApprovals.forEach((updatedApproval) => {
      const index = this.approvals.findIndex(
        (approval: any) => approval.user_id === updatedApproval?.user_id
      );
      if (index !== -1) {
        // Update the existing approval with the new data
        this.approvals[index]['feedback'] = updatedApproval?.feedback;
        this.approvals[index]['status'] = updatedApproval?.status;
      }
    });
  }
  isUserApprover(): boolean {
    return this.approvalDetails?.some(
      (user) => user.requestedApproverId === this.user_info?.id && user.status === 'pending'
    );
  }
}
